import { useEffect, useRef, useState } from 'react';
import { getImageUrl } from '../utils/imageUtils';

// Global overlay management to prevent multiple overlays
let activeOverlays = 0;
let overlayTimeout: NodeJS.Timeout | null = null;
let lastOverlayTime = 0;
const OVERLAY_COOLDOWN = 500; // Minimum time between overlays in ms

interface UseOptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  withCardOverlay?: boolean;
}

interface UseOptimizedImageReturn {
  imgRef: React.RefObject<HTMLImageElement>;
  isLoading: boolean;
  hasError: boolean;
  optimizedSrc: string;
  cardRef?: React.RefObject<HTMLDivElement>;
  overlayRef?: React.RefObject<HTMLDivElement>;
  loadingState?: { isLoading: boolean; progress: number; error: boolean };
}

export const useOptimizedImage = ({
  src,
  alt,
  className = '',
  onLoad,
  onError,
  withCardOverlay = false
}: UseOptimizedImageProps): UseOptimizedImageReturn => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [optimizedSrc, setOptimizedSrc] = useState(src);

  // Card overlay logic
  const cardRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState({ isLoading: true, progress: 0, error: false });

  // NEW: Check if user is authenticated and use direct image URL
  const isAuthenticated = () => {
    return !!localStorage.getItem('token');
  };

  // NEW: Get optimized source URL using getImageUrl
  const getOptimizedSource = (originalSrc: string): string => {
    // Use the unified getImageUrl function from imageUtils
    return getImageUrl(originalSrc);
  };

  // --- Restore overlay helper functions ---
  const createOverlay = () => {
    const overlay = overlayRef.current!;
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.background = 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 50%, rgba(15, 52, 96, 0.95) 100%)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.style.zIndex = '9999';
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s ease-in-out';
    overlay.style.borderRadius = '15%';
    overlay.style.backdropFilter = 'blur(2px)';
    overlay.style.pointerEvents = 'none';
    // Create icon
    const icon = document.createElement('img');
    icon.src = '/img/AeLogo.ico';
    icon.style.width = '50px';
    icon.style.height = '50px';
    icon.style.marginTop = '20%';
    icon.style.animation = 'cardBounce 1.2s ease-in-out infinite, cardGlow 2s ease-in-out infinite';
    icon.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
    icon.style.borderRadius = '15%';
    icon.style.background = 'rgba(255, 255, 255, 0.1)';
    icon.style.padding = '8px';
    icon.alt = 'Loading...';
    // Create progress container
    const progressContainer = document.createElement('div');
    progressContainer.style.position = 'absolute';
    progressContainer.style.bottom = '10px';
    progressContainer.style.left = '10px';
    progressContainer.style.right = '10px';
    progressContainer.style.height = '3px';
    progressContainer.style.background = 'rgba(255, 255, 255, 0.2)';
    progressContainer.style.borderRadius = '2px';
    progressContainer.style.overflow = 'hidden';
    // Create progress fill
    const progressFill = document.createElement('div');
    progressFill.style.height = '100%';
    progressFill.style.background = 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)';
    progressFill.style.width = '0%';
    progressFill.style.transition = 'width 0.3s ease-out';
    progressFill.style.borderRadius = '2px';
    progressFill.style.boxShadow = '0 1px 4px rgba(102, 126, 234, 0.5)';
    // Create text
    const loadingText = document.createElement('div');
    loadingText.innerHTML = 'Loading...';
    loadingText.style.color = 'white';
    loadingText.style.fontFamily = 'Arial, sans-serif';
    loadingText.style.fontSize = '14px';
    loadingText.style.fontWeight = 'bold';
    loadingText.style.textAlign = 'center';
    loadingText.style.marginTop = '12px';
    loadingText.style.opacity = '0.95';
    loadingText.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.7)';
    loadingText.style.background = 'rgba(0, 0, 0, 0.3)';
    loadingText.style.padding = '4px 8px';
    loadingText.style.borderRadius = '4px';
    // Assemble overlay
    progressContainer.appendChild(progressFill);
    overlay.appendChild(icon);
    overlay.appendChild(progressContainer);
    overlay.appendChild(loadingText);
    return { progressFill, loadingText };
  };
  const addAnimations = () => {
    // Animations are now in index.css, so no need to inject styles dynamically
  };
  const showLoading = () => {
    const overlay = overlayRef.current;
    if (!overlay) {
      return;
    }
    
    const now = Date.now();
    
    // Prevent multiple overlays from showing simultaneously
    if (activeOverlays > 0) {
      return;
    }
    
    // Prevent rapid overlay creation
    if (now - lastOverlayTime < OVERLAY_COOLDOWN) {
      return;
    }
    
    activeOverlays++;
    lastOverlayTime = now;
    overlay.style.opacity = '1';
    // Start progress animation
    let progress = 0;
    const progressFill = overlay.querySelector('div');
    const loadingText = overlay.querySelector('div + div');
    const interval = setInterval(() => {
      progress += Math.random() * 20;
      if (progress > 90) progress = 90;
      if (progressFill) {
        (progressFill as HTMLElement).style.width = `${progress}%`;
      }
      if (loadingText) {
        (loadingText as HTMLElement).innerHTML = `Loading... ${Math.round(progress)}%`;
      }
      setLoadingState(prev => ({ ...prev, progress: Math.round(progress) }));
      if (progress >= 90) {
        clearInterval(interval);
      }
    }, 300);
  };
  const hideLoading = () => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    
    activeOverlays = Math.max(0, activeOverlays - 1);
    
    setTimeout(() => {
      if (overlay && overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }
    }, 300);
  };

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Reset states
    setIsLoading(true);
    setHasError(false);
    
    // NEW: Use optimized source for authenticated users
    const finalSrc = getOptimizedSource(src);
    setOptimizedSrc(finalSrc);

    // Add loading class
    img.classList.add('image-loading');

    // Handle load event
    const handleLoad = () => {
      setIsLoading(false);
      img.classList.remove('image-loading');
      img.classList.add('image-loaded');
      onLoad?.();
    };

    // Handle error event
    const handleError = () => {
      setIsLoading(false);
      setHasError(true);
      img.classList.remove('image-loading');
      img.classList.add('image-error');
      onError?.();
    };

    // Add event listeners
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    // Image optimization is now handled by getImageUrl in imageUtils

    // Cleanup
    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [src, onLoad, onError]);

  // Card overlay effect (only if withCardOverlay is true)
  useEffect(() => {
    if (!withCardOverlay) return;
    
    // Add delay to prevent rapid overlay creation
    const timer = setTimeout(() => {
      if (!cardRef.current) {
        return;
      }
      const card = cardRef.current;
      const overlay = overlayRef.current;
      if (!overlay) {
        return;
      }
      
      // Clear any existing timeout
      if (overlayTimeout) {
        clearTimeout(overlayTimeout);
      }
      
      // Add additional delay to prevent multiple overlays
      overlayTimeout = setTimeout(() => {
        const { progressFill, loadingText } = createOverlay();
        addAnimations();
        showLoading();
        // Restore previous logic: use a separate window.Image for overlay timing
        const img = new window.Image();
        img.onload = () => {
          if (progressFill && progressFill.style) {
            progressFill.style.width = '100%';
          }
          if (loadingText && loadingText.innerHTML !== undefined) {
            loadingText.innerHTML = 'Loaded!';
            loadingText.style.color = '#90EE90';
          }
          setLoadingState({ isLoading: false, progress: 100, error: false });
          hideLoading(); // Remove overlay immediately
        };
        img.onerror = () => {
          if (loadingText && loadingText.innerHTML !== undefined) {
            loadingText.innerHTML = 'Failed to load';
            loadingText.style.color = '#ff6b6b';
          }
          setLoadingState({ isLoading: false, progress: 0, error: true });
          hideLoading(); // Remove overlay immediately
        };
        // Add null check before setting src
        if (img && src) {
          // NEW: Use optimized source for authenticated users in overlay
          const finalSrc = getOptimizedSource(src);
          img.src = finalSrc;
        }
      }, 150); // Increased delay to 150ms
    }, 100); // Increased initial delay to 100ms
    
    return () => {
      clearTimeout(timer);
      if (overlayTimeout) {
        clearTimeout(overlayTimeout);
      }
      const overlay = overlayRef.current;
      if (overlay && overlay.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }
    };
  }, [src, withCardOverlay]);

  if (withCardOverlay) {
    return {
      imgRef,
      isLoading,
      hasError,
      optimizedSrc,
      cardRef,
      overlayRef,
      loadingState
    };
  }
  return {
    imgRef,
    isLoading,
    hasError,
    optimizedSrc
  };
}; 